import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Card } from '@/components/ui/card';
import { Loader2, UserPlus, X, Check } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { formatDistanceToNow } from 'date-fns';

interface FollowRequest {
  id: string;
  requester_id: string;
  created_at: string;
  requester_profile?: {
    username: string;
    name: string | null;
    image: string | null;
  };
}

interface FollowRequestsListProps {
  onRequestHandled?: () => void;
}

export function FollowRequestsList({ onRequestHandled }: FollowRequestsListProps) {
  const [requests, setRequests] = useState<FollowRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchRequests = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: requestsData, error: requestsError } = await supabase
        .from('follow_requests')
        .select('id, requester_id, created_at')
        .eq('requested_id', user.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (requestsError) throw requestsError;

      // Fetch profiles separately
      const requesterIds = requestsData?.map(r => r.requester_id) || [];
      if (requesterIds.length === 0) {
        setRequests([]);
        return;
      }

      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, username, name, image')
        .in('id', requesterIds);

      if (profilesError) throw profilesError;

      // Combine the data
      const enrichedRequests = requestsData?.map(req => ({
        ...req,
        requester_profile: profilesData?.find(p => p.id === req.requester_id)
      })) || [];

      setRequests(enrichedRequests);
    } catch (error) {
      console.error('Error fetching follow requests:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();

    // Set up real-time subscription
    const channel = supabase
      .channel('follow-requests-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'follow_requests'
        },
        () => {
          fetchRequests();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const handleResponse = async (requestId: string, response: 'accepted' | 'rejected') => {
    setProcessingId(requestId);
    try {
      const { error } = await supabase.rpc('respond_to_follow_request', {
        request_id: requestId,
        response: response
      });

      if (error) throw error;

      toast({
        title: response === 'accepted' ? 'Request Accepted' : 'Request Rejected',
        description: response === 'accepted' 
          ? 'You have a new follower!' 
          : 'Follow request declined',
      });

      // Remove from list immediately
      setRequests(prev => prev.filter(req => req.id !== requestId));
      
      // Notify parent component
      if (onRequestHandled) {
        onRequestHandled();
      }
    } catch (error) {
      console.error('Error responding to follow request:', error);
      toast({
        title: 'Error',
        description: 'Failed to process follow request',
        variant: 'destructive',
      });
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <UserPlus className="h-12 w-12 mx-auto mb-3 opacity-20" />
        <p className="text-sm">No pending follow requests</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {requests.map((request) => {
        const profile = request.requester_profile;
        if (!profile) return null;

        return (
          <Card key={request.id} className="p-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <Avatar className="h-10 w-10 flex-shrink-0">
                  <AvatarImage src={profile.image || undefined} />
                  <AvatarFallback>
                    {profile.username?.[0]?.toUpperCase() || '?'}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm truncate">
                    {profile.name || profile.username}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    @{profile.username}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {formatDistanceToNow(new Date(request.created_at), { addSuffix: true })}
                  </p>
                </div>
              </div>
            <div className="flex gap-2 flex-shrink-0">
              <Button
                size="sm"
                variant="default"
                onClick={() => handleResponse(request.id, 'accepted')}
                disabled={processingId === request.id}
              >
                {processingId === request.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-1" />
                    Accept
                  </>
                )}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleResponse(request.id, 'rejected')}
                disabled={processingId === request.id}
              >
                <X className="h-4 w-4 mr-1" />
                Reject
              </Button>
            </div>
          </div>
        </Card>
      );
      })}
    </div>
  );
}
